import { AppVersionService } from '@/http-services/app-version.service';
import { App, AppInfo } from '@capacitor/app';
import { AlertButton, alertController, isPlatform } from '@ionic/vue';
import { defineStore } from 'pinia';
import { ref } from 'vue';
import * as LiveUpdates from '@capacitor/live-updates';
import { captureException } from '@sentry/vue';

export const useAppInfo = defineStore('app-info', () => {
  const appInfo = ref<AppInfo>();
  /**
   * The apps current version. Derived from package.json.
   */
  const appVersion = `v${__APP_VERSION__}`;
  /**
   * The latest cached version available from the server.
   */
  const latestVersion = ref<string | null>(null);
  let appUpdateInterval: NodeJS.Timeout | null = null;
  const versionService = new AppVersionService();
  const userChannel = ref<string | null>(null);

  if (isPlatform('hybrid')) {
    App.getInfo().then((info) => {
      console.log('APP INFO', info);
      appInfo.value = info;
    });
  }

  /**
   * Start a 5 minute interval to periodically check if the app should be updated.
   *
   * This will prompt the user to restart the app if there is a new live update.
   */
  function onAppResume() {
    if (!appUpdateInterval) {
      appUpdateInterval = setInterval(async () => {
        const shouldUpdate = localStorage.getItem('shouldReloadApp') === 'true';

        if (shouldUpdate) {
          // prompt user to restart app
          await alertController.create({
            cssClass: 'ncss-alert',
            header: 'Update Available',
            message: `Version ${latestVersion} of the app is available. Please restart the app to apply the update.`,
            buttons: [
              {
                text: 'Restart',
                handler: () => {
                  LiveUpdates.reload();
                },
              },
              {
                text: 'Cancel',
                role: 'cancel',
              },
            ],
          });

          localStorage.removeItem('shouldReloadApp');
        }
      }, 1000 * 60 * 5);
    }
  }

  /**
   * Cleanup the app update interval.
   */
  function stopLiveUpdateCheckInterval() {
    if (appUpdateInterval) {
      clearInterval(appUpdateInterval);
      appUpdateInterval = null;
    }
  }

  /**
   * Prompt the user with a capacitor alert to update the app.
   * @param isRequired
   * @param requiredVersion
   */
  function promptUserToUpdate(isRequired: boolean, requiredVersion: string) {
    const buttons: AlertButton[] = [
      {
        text: 'Update',
        handler: () => {
          window.open(getAppUrl(), '_system');

          return !isRequired;
        },
      },
    ];

    if (!isRequired) {
      buttons.push({
        text: 'Cancel',
        role: 'cancel',
      });
    }

    return alertController
      .create({
        cssClass: 'ncss-alert',
        header: isRequired ? 'Update Required' : 'Update Available',
        message: isRequired
          ? `It looks like you are using version ${appVersion} of the NCSS Mobile App. To continue, please update to version v${requiredVersion}.`
          : 'There is a new version available, would you like to update to the latest version?',
        buttons,
        backdropDismiss: !isRequired,
      })
      .then((alert) => alert.present());
  }

  /**
   * Set the user channel for live updates. This requires the user to be logged in and authenticated.
   */
  async function setUserChannel() {
    const { data } = await versionService.getUserChannel();
    const channel = import.meta.env.DEV ? 'DEV' : data;
    userChannel.value = channel;
    LiveUpdates.setConfig({ channel });
  }

  /**
   * Retrun the app store URL based on the platform.
   * @returns
   */
  function getAppUrl() {
    if (isPlatform('android')) {
      return 'market://details?id=com.nextcenturymeters.ncssmobile';
    }

    return 'https://apps.apple.com/us/app/ncss/id994253847';
  }

  /**
   * Check the app version and prompt the user to update if a newer version is available.
   */
  async function checkVersion() {
    try {
      const version = await versionService.getAppVersion();
      if (!version.isSuccess || !version.data) {
        console.log('Skipping version check with', version);
        return;
      }
      if (isVersionNewer(version.data.minimumRequiredVersion)) {
        latestVersion.value = `v${version.data.minimumRequiredVersion}`;
        return promptUserToUpdate(true, version.data.minimumRequiredVersion);
      }
      if (isVersionNewer(version.data.latestVersion)) {
        latestVersion.value = `v${version.data.latestVersion}`;
        return promptUserToUpdate(false, version.data.latestVersion);
      }
    } catch (e) {
      console.error('Error checking app version', e);
      captureException(e);
    }
  }

  /**
   * Compare 2 version values as strings.
   *
   * Accepted version formats:
   * - v4.0.12
   * - 4.0.12
   * @param version1 Target version
   * @param version2 Current version
   * @returns
   */
  function isVersionNewer(version1: string, version2: string = appVersion): boolean {
    return compareVersions(version1, version2) > 0;
  }

  function compareVersions(version1: string, version2: string): number {
    const v1Parts = version1.replace('v', '').split('.').map(Number);
    const v2Parts = version2.replace('v', '').split('.').map(Number);

    const maxLength = Math.max(v1Parts.length, v2Parts.length);

    for (let i = 0; i < maxLength; i++) {
      const v1 = v1Parts[i] || 0;
      const v2 = v2Parts[i] || 0;

      if (v1 > v2) return 1;
      if (v1 < v2) return -1;
    }

    return 0;
  }

  return {
    appInfo,
    appVersion,
    userChannel,
    onAppResume,
    stopLiveUpdateCheckInterval,
    checkVersion,
    isVersionNewer,
    setUserChannel,
  };
});
